import React, { useEffect, useState, useRef } from 'react';
import { X, Download, FileText, CheckCircle, Loader2, Mail } from 'lucide-react';
import { motion } from 'framer-motion';
interface DownloadReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  reportData: {
    type: 'personal' | 'business';
    score: number;
    sectionScores: number[];
    sections: any[];
    actionPlanItems: any[];
    setupData: any;
    answers: Record<string, string>;
  };
}
export function DownloadReportModal({
  isOpen,
  onClose,
  reportData
}: DownloadReportModalProps) {
  const [step, setStep] = useState<'options' | 'generating' | 'success'>('options');
  const [generatingMessage, setGeneratingMessage] = useState('Preparing your report...');
  const [options, setOptions] = useState({
    overallScore: true,
    categoryScores: true,
    topIssues: true,
    actionPlan: true,
    recommendations: true,
    dateCompleted: true,
    userName: false,
    disclaimer: true
  });
  const printRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isOpen) {
      setStep('options');
      setOptions({
        overallScore: true,
        categoryScores: true,
        topIssues: true,
        actionPlan: true,
        recommendations: true,
        dateCompleted: true,
        userName: false,
        disclaimer: true
      });
    }
  }, [isOpen]);
  if (!isOpen) return null;
  const fileName = `lawvo-${reportData.type}-legal-health-check.pdf`;
  const reportTitle = `${reportData.type === 'personal' ? 'Personal' : 'Business'} Legal Health Check Report`;
  const themeColor = reportData.type === 'personal' ? 'text-violetBlue' : 'text-turquoise';
  const themeBg = reportData.type === 'personal' ? 'bg-violetBlue' : 'bg-turquoise';
  const scoreColor = reportData.score >= 70 ? 'green' : reportData.score >= 40 ? 'amber' : 'red';
  const scoreLabel = reportData.score >= 70 ? 'Strong Foundation' : reportData.score >= 40 ? 'Needs Review' : 'Action Recommended';
  const handleGenerate = () => {
    setStep('generating');
    setGeneratingMessage('Preparing your report...');
    setTimeout(() => {
      setGeneratingMessage('Generating branded PDF...');
      setTimeout(() => {
        setStep('success');
      }, 1500);
    }, 1500);
  };
  const handleDownload = () => {
    if (!printRef.current) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const cssStyles = `
      *, body {
        font-family: 'Futura', 'Helvetica', Arial, sans-serif;
        color: #101D5B;
        margin: 0;
        padding: 0;
      }
      body { padding: 40px; }
      .header {
        text-align: center;
        margin-bottom: 40px;
        border-bottom: 3px solid #7854FF;
        padding-bottom: 24px;
      }
      .logo { height: 32px; margin-bottom: 16px; }
      .title { font-size: 24px; font-weight: bold; margin: 0 0 8px; }
      .subtitle { font-size: 14px; color: #7E8299; }
      .score-section {
        text-align: center;
        margin: 32px 0;
        padding: 24px;
        background: #F6F9FC;
        border-radius: 12px;
      }
      .score-value { font-size: 48px; font-weight: bold; }
      .score-label {
        font-size: 14px;
        font-weight: bold;
        text-transform: uppercase;
        letter-spacing: 1px;
      }
      .section { margin: 24px 0; }
      .section-title {
        font-size: 18px;
        font-weight: bold;
        margin-bottom: 12px;
        padding-bottom: 8px;
        border-bottom: 1px solid #E8EEF4;
      }
      .category-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px 0;
        border-bottom: 1px solid #E8EEF4;
      }
      .category-name { font-size: 14px; }
      .category-score { font-size: 14px; font-weight: bold; }
      .issue-item {
        padding: 8px 0;
        font-size: 14px;
        border-bottom: 1px solid #E8EEF4;
      }
      .action-item {
        padding: 10px 0;
        border-bottom: 1px solid #E8EEF4;
      }
      .action-text { font-size: 14px; font-weight: 600; }
      .action-meta { font-size: 12px; color: #7E8299; margin-top: 4px; }
      .priority-high { color: #FC5F7A; }
      .priority-medium { color: #F59E0B; }
      .priority-low { color: #3B82F6; }
      .footer {
        margin-top: 40px;
        padding-top: 20px;
        border-top: 2px solid #7854FF;
        text-align: center;
        font-size: 11px;
        color: #7E8299;
      }
      .disclaimer {
        margin-top: 32px;
        padding: 16px;
        background: #FEF3C7;
        border-radius: 8px;
        font-size: 12px;
        color: #92400E;
      }
      .green { color: #22C55E; }
      .amber { color: #F59E0B; }
      .red { color: #FC5F7A; }
      @media print { body { padding: 20px; } }
    `;
    printWindow.document.write('<html><head><title>' + reportTitle + '</title><style>{`' + cssStyles + '`}</style></head><body>' + printRef.current.innerHTML + '</body></html>');
    printWindow.document.close();
    printWindow.print();
  };
  const handleToggle = (key: keyof typeof options) => {
    setOptions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  return <>
      <div className="fixed inset-0 bg-black/50 z-50 transition-opacity" onClick={onClose} data-id="element-514" />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white rounded-2xl shadow-xl z-50 overflow-hidden flex flex-col max-h-[90vh]" data-id="element-515">
        {/* Header */}
        <div className="px-6 py-4 border-b border-borderColor flex items-center justify-between bg-white sticky top-0 z-10" data-id="element-516">
          <h2 className="text-xl font-bold text-primary" data-id="element-517">Download Report</h2>
          <button onClick={onClose} className="p-2 hover:bg-aliceBlue rounded-lg transition-colors" data-id="element-518">
            <X className="w-5 h-5 text-secondary" data-id="element-519" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto" data-id="element-520">
          {step === 'options' && <div className="space-y-6" data-id="element-521">
              <p className="text-sm text-secondary" data-id="element-522">
                Export a branded PDF copy of your Legal Health Check report.
              </p>

              <div className="bg-aliceBlue rounded-xl p-4 flex items-center gap-3 border border-borderColor" data-id="element-523">
                <div className={`p-2 rounded-lg bg-white shadow-sm ${themeColor}`} data-id="element-524">
                  <FileText className="w-6 h-6" data-id="element-525" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-526">
                  <p className="text-sm font-bold text-primary truncate" data-id="element-527">
                    {reportTitle}
                  </p>
                  <p className="text-xs text-secondary truncate" data-id="element-528">{fileName}</p>
                </div>
              </div>

              <div className="space-y-3" data-id="element-529">
                <h3 className="text-sm font-semibold text-primary mb-2" data-id="element-530">
                  Export Options
                </h3>
                {([['overallScore', 'Include overall score'], ['categoryScores', 'Include category scores'], ['topIssues', 'Include top priority issues'], ['actionPlan', 'Include action plan'], ['recommendations', 'Include LawVo recommendations']] as const).map(([key, label]) => <label key={key} className="flex items-center gap-3 cursor-pointer" data-id="element-531">
                    <input type="checkbox" checked={options[key]} onChange={() => handleToggle(key)} className="w-4 h-4 text-violetBlue rounded border-borderColor focus:ring-violetBlue" data-id="element-532" />
                    <span className="text-sm text-primary" data-id="element-533">{label}</span>
                  </label>)}

                <div className="pt-2 border-t border-borderColor space-y-3" data-id="element-534">
                  {([['dateCompleted', 'Include date completed'], ['userName', `Include ${reportData.type === 'business' ? 'business' : 'user'} name`], ['disclaimer', 'Include disclaimer page']] as const).map(([key, label]) => <div key={key} className="flex items-center justify-between" data-id="element-535">
                      <span className="text-sm text-primary" data-id="element-536">{label}</span>
                      <button onClick={() => handleToggle(key)} className={`relative w-11 h-6 rounded-full transition-colors ${options[key] ? 'bg-turquoise' : 'bg-disabled'}`} data-id="element-537">
                        <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${options[key] ? 'translate-x-5' : ''}`} data-id="element-538" />
                      </button>
                    </div>)}
                </div>
              </div>

              <div className="flex gap-3 pt-2" data-id="element-539">
                <button onClick={onClose} className="flex-1 px-4 py-3 border border-borderColor text-primary rounded-lg hover:bg-aliceBlue transition-colors text-sm font-medium" data-id="element-540">
                  Cancel
                </button>
                <button onClick={handleGenerate} className={`flex-1 px-4 py-3 ${themeBg} text-white rounded-lg hover:opacity-90 transition-colors text-sm font-medium flex items-center justify-center gap-2`} data-id="element-541">
                  <FileText className="w-4 h-4" data-id="element-542" />
                  Generate PDF
                </button>
              </div>
            </div>}

          {step === 'generating' && <div className="flex flex-col items-center justify-center py-12" data-id="element-543">
              <div className={`w-16 h-16 rounded-full ${reportData.type === 'personal' ? 'bg-aliceBlue' : 'bg-turquoise/10'} flex items-center justify-center mb-6`} data-id="element-544">
                <Loader2 className={`w-8 h-8 ${themeColor} animate-spin`} data-id="element-545" />
              </div>
              <h3 className="text-lg font-bold text-primary mb-2" data-id="element-546">
                Generating Report
              </h3>
              <p className="text-sm text-secondary animate-pulse" data-id="element-547">
                {generatingMessage}
              </p>
            </div>}

          {step === 'success' && <div className="flex flex-col items-center justify-center py-8" data-id="element-548">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 20
          }} className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-6" data-id="element-549">
                <CheckCircle className="w-8 h-8 text-green-500" data-id="element-550" />
              </motion.div>
              <h3 className="text-lg font-bold text-primary mb-2" data-id="element-551">PDF Ready</h3>
              <p className="text-sm text-secondary mb-6" data-id="element-552">
                Your branded report has been generated.
              </p>

              <div className="w-full space-y-3" data-id="element-553">
                <button onClick={handleDownload} className={`w-full px-4 py-3 ${themeBg} text-white rounded-lg hover:opacity-90 transition-colors text-sm font-medium flex items-center justify-center gap-2`} data-id="element-554">
                  <Download className="w-4 h-4" data-id="element-555" />
                  Download Now
                </button>
                <button onClick={() => {
              onClose();
            }} className="w-full px-4 py-3 bg-aliceBlue text-primary rounded-lg hover:bg-borderColor transition-colors text-sm font-medium flex items-center justify-center gap-2" data-id="element-556">
                  <Mail className="w-4 h-4" data-id="element-557" />
                  Email Copy to Me
                </button>
                <button onClick={onClose} className="w-full px-4 py-2 text-secondary hover:text-primary transition-colors text-sm font-medium" data-id="element-558">
                  Close
                </button>
              </div>
            </div>}
        </div>
      </div>

      {/* Hidden PDF Content */}
      <div ref={printRef} className="hidden" data-id="element-559">
        <div className="header" data-id="element-560">
          <img src="https://cdn.magicpatterns.com/uploads/u4AKTM42BTBGHrLv1PiYxT/Logo_3.png" alt="LawVo" className="logo" data-id="element-561" />
          <div className="title" data-id="element-562">{reportTitle}</div>
          <div className="subtitle" data-id="element-563">
            {options.dateCompleted && `Generated on ${new Date().toLocaleDateString('en-CA', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}`}
          </div>
        </div>

        {options.overallScore && <div className="score-section" data-id="element-564">
            <div className={`score-value ${scoreColor}`} data-id="element-565">
              {reportData.score}
            </div>
            <div className={`score-label ${scoreColor}`} data-id="element-566">{scoreLabel}</div>
            <div className="subtitle" data-id="element-567">out of 100</div>
          </div>}

        {options.categoryScores && <div className="section" data-id="element-568">
            <div className="section-title" data-id="element-569">Category Scores</div>
            {reportData.sections.map((sec: any, idx: number) => <div key={sec.id} className="category-row" data-id="element-570">
                <span className="category-name" data-id="element-571">{sec.title}</span>
                <span className={`category-score ${(reportData.sectionScores[idx] || 0) >= 70 ? 'green' : (reportData.sectionScores[idx] || 0) >= 40 ? 'amber' : 'red'}`} data-id="element-572">
                  {reportData.sectionScores[idx] || 0}%
                </span>
              </div>)}
          </div>}

        {options.topIssues && <div className="section" data-id="element-573">
            <div className="section-title" data-id="element-574">Top Priority Issues</div>
            {reportData.actionPlanItems.filter((item: any) => item.priority === 'high').slice(0, 5).map((item: any, idx: number) => <div key={idx} className="issue-item" data-id="element-575">
                  <span className="priority-high" data-id="element-576">●</span> {item.text}
                </div>)}
            {reportData.actionPlanItems.filter((item: any) => item.priority === 'high').length === 0 && <div className="issue-item green" data-id="element-577">No critical issues found.</div>}
          </div>}

        {options.actionPlan && <div className="section" data-id="element-578">
            <div className="section-title" data-id="element-579">Action Plan</div>
            {reportData.actionPlanItems.map((item: any, idx: number) => <div key={idx} className="action-item" data-id="element-580">
                <div className="action-text" data-id="element-581">
                  <span className={`priority-${item.priority}`} data-id="element-582">●</span>{' '}
                  {item.text}
                </div>
                <div className="action-meta" data-id="element-583">
                  {item.category} · Effort: {item.effort} · Est. time:{' '}
                  {item.time}
                </div>
              </div>)}
          </div>}

        {options.recommendations && <div className="section" data-id="element-584">
            <div className="section-title" data-id="element-585">Recommended Next Steps</div>
            <div className="issue-item" data-id="element-586">
              Browse LawVo's template library to create missing documents.
            </div>
            <div className="issue-item" data-id="element-587">
              Use LawVo AI Copilot for immediate guidance on your results.
            </div>
            <div className="issue-item" data-id="element-588">
              Book a consultation with a qualified lawyer for high-priority
              issues.
            </div>
          </div>}

        {options.disclaimer && <div className="disclaimer" data-id="element-589">
            <strong data-id="element-590">Disclaimer:</strong> This report is generated by LawVo for
            informational purposes only. It does not constitute legal advice and
            should not be relied upon as a substitute for consultation with a
            qualified lawyer licensed in your province. Your results are based
            on self-reported answers and may not reflect your complete legal
            situation.
          </div>}

        <div className="footer" data-id="element-591">
          <p data-id="element-592">© {new Date().getFullYear()} LawVo Inc. All rights reserved.</p>
          <p data-id="element-593">
            This report is confidential and intended for the recipient only.
          </p>
          <p data-id="element-594">lawvo.com</p>
        </div>
      </div>
    </>;
}